import React, { useCallback, useEffect, useState } from "react";
import moment from "moment-timezone";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import {
  getMapSnapshots,
  getSnapshotSignedUrl,
  deleteMapSnapshot,
  uploadMapSnapshot,
  MulchMapSnapshotSummary,
} from "api/MulchMap";

/**
 * MapSnapshotsModal \u2014 Session 24 addendum
 *
 * One-stop UI for snapshots on a specific map version. Reps hit
 * \"Capture current view\" to render + save; existing snapshots list
 * below with signed thumbnails and delete affordances. Signed URLs
 * are fetched per-row on mount rather than baked into the list
 * response so they don't expire between fetch and render.
 *
 * viewState comes in from the caller \u2014 the MapEditor reads it off
 * its Google Maps instance ref at the moment the modal opens. This
 * keeps the modal decoupled from Google Maps internals; if the map
 * ref isn't ready, the button gets disabled with a hint.
 */

interface Props {
  mulchMapId: string | null; // null when the map hasn't been saved yet
  /**
   * Session 28: caller renders the current map view to a PNG Blob and
   * hands back the bytes + a viewState metadata object. Modal then
   * uploads to the backend. Returning null signals the map isn't ready
   * yet (2D ref not attached, 3D shadow DOM not painted, etc.) and the
   * modal surfaces a friendly retry hint.
   */
  captureCanvas: () => Promise<{
    blob: Blob;
    viewState: {
      centerLat: number;
      centerLng: number;
      zoom: number;
      tilt?: number;
      heading?: number;
      basemap?: string;
    };
  } | null>;
  /**
   * Session 28 hotfix 5: when set, disables the Capture button and shows
   * this text in the capture row instead. Used to communicate
   * environmental constraints \u2014 e.g. Google's Map3DElement uses a closed
   * shadow DOM which browsers make architecturally unreadable, so 3D
   * snapshots are impossible from the current tab. Reps switch to a 2D
   * basemap to capture.
   */
  captureDisabledReason?: string | null;
  /**
   * Session 28 hotfix 8: informational note that renders alongside the
   * Capture button (not in place of it). Used when capture works but
   * involves an extra step \u2014 e.g. 3D capture goes through the
   * browser\u2019s screen-share prompt.
   */
  captureNoticeText?: string | null;
  onClose: () => void;
}

interface Row {
  snapshot: MulchMapSnapshotSummary;
  signedUrl: string | null;
  urlLoading: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  intro: {
    fontSize: 13,
    color: "#5D5E62",
    marginTop: 0,
    marginBottom: 12,
    lineHeight: 1.5,
  },
  captureRow: {
    padding: 12,
    background: "#F9FAFB",
    border: "1px solid #E4E7EB",
    borderRadius: 8,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  listLabel: {
    fontSize: 11,
    color: "#B0B4BA",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
    marginBottom: 8,
  },
  emptyList: {
    fontSize: 12,
    color: "#78716C",
    fontStyle: "italic",
    padding: "16px 8px",
    textAlign: "center",
  },
  snapshotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 12,
  },
  snapshotCard: {
    border: "1px solid #E4E7EB",
    borderRadius: 8,
    overflow: "hidden",
    background: "#FFF",
  },
  thumb: {
    width: "100%",
    aspectRatio: "4 / 3",
    background: "#F3F4F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    color: "#78716C",
    cursor: "pointer",
    position: "relative",
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  // Session 28 hotfix 3: basemap label pill in the top-left of the thumb.
  basemapPill: {
    position: "absolute",
    top: 6,
    left: 6,
    padding: "2px 6px",
    background: "rgba(17, 17, 17, 0.75)",
    color: "#FFF",
    borderRadius: 3,
    fontSize: 9,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    pointerEvents: "none",
  },
  cardFoot: {
    padding: "8px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 11,
    color: "#5D5E62",
  },
  cardFootRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 6,
  },
  timestampAbsolute: {
    fontSize: 10,
    color: "#8A8B8F",
  },
  deleteBtn: {
    background: "transparent",
    border: "1px solid #FCA5A5",
    color: "#991B1B",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 10,
    cursor: "pointer",
  },
  errorBox: {
    marginTop: 12,
    padding: "10px 12px",
    background: "#FEE2E2",
    border: "1px solid #FCA5A5",
    borderRadius: 6,
    fontSize: 12,
    color: "#991B1B",
  },
};

// Session 28 hotfix 3: pretty labels for the basemap pill on each card.
const BASEMAP_LABELS: Record<string, string> = {
  google_hybrid: "Google 2D",
  mapbox_sat: "Mapbox",
  esri_sat: "Esri",
  google_3d: "Google 3D",
};

const MapSnapshotsModal: React.FC<Props> = ({
  mulchMapId,
  captureCanvas,
  captureDisabledReason,
  captureNoticeText,
  onClose,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSnapshots = useCallback(async () => {
    if (!mulchMapId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const snaps = await getMapSnapshots(mulchMapId);
    // Seed rows with urls unloaded so the grid renders immediately.
    // Then fetch signed URLs in parallel, updating each row as it lands.
    const initial: Row[] = snaps.map((s) => ({
      snapshot: s,
      signedUrl: null,
      urlLoading: true,
    }));
    setRows(initial);
    setLoading(false);

    // Signed-URL hydration \u2014 5-minute default expiry, plenty for the
    // duration of the modal being open. If the rep leaves the modal open
    // longer than that, the images will silently fail to load on
    // re-render; reopening the modal refetches. Acceptable trade-off.
    await Promise.all(
      snaps.map(async (s, i) => {
        const url = await getSnapshotSignedUrl(s.id);
        setRows((prev) => {
          const next = [...prev];
          if (next[i] && next[i].snapshot.id === s.id) {
            next[i] = { ...next[i], signedUrl: url, urlLoading: false };
          }
          return next;
        });
      }),
    );
  }, [mulchMapId]);

  useEffect(() => {
    loadSnapshots();
  }, [loadSnapshots]);

  const handleCapture = async () => {
    if (!mulchMapId) {
      setError(
        "Save this map to a site before capturing a snapshot. The snapshot needs a specific version to attach to.",
      );
      return;
    }
    setCapturing(true);
    setError(null);
    const captured = await captureCanvas();
    if (!captured) {
      setError(
        "Map isn't ready yet \u2014 try again in a moment once the site view has loaded.",
      );
      setCapturing(false);
      return;
    }
    const res = await uploadMapSnapshot(
      mulchMapId,
      captured.blob,
      captured.viewState,
    );
    if ("error" in res) {
      setError(res.error);
      setCapturing(false);
      return;
    }
    // Refetch the whole list \u2014 simpler than trying to synchronously
    // resolve the signed URL for just the new row.
    await loadSnapshots();
    setCapturing(false);
  };

  const handleDelete = async (snapshotId: string) => {
    const ok = window.confirm(
      "Delete this snapshot? The image and DB record are both removed.",
    );
    if (!ok) return;
    const res = await deleteMapSnapshot(snapshotId);
    if ("error" in res) {
      setError(res.error);
      return;
    }
    setRows((prev) => prev.filter((r) => r.snapshot.id !== snapshotId));
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>Map snapshots</ModalHeader>
      <ModalBody>
        <p style={styles.intro}>
          Snapshots capture the current map view (satellite base + zone
          polygons) as a PNG for use later in Quote and Invoice PDFs.
          Each snapshot pins to this specific map version — if you save
          a new version, capture a fresh snapshot for that version.
        </p>

        <div style={styles.captureRow}>
          <div style={{ fontSize: 13, color: "#111" }}>
            {captureDisabledReason
              ? captureDisabledReason
              : captureNoticeText
                ? captureNoticeText
                : "Capture the map exactly as it appears right now."}
          </div>
          {!captureDisabledReason && (
            <BasicButton
              type="button"
              onClick={handleCapture}
              disabled={capturing || !mulchMapId}
              styles={{ margin: "0" }}
              keepText
            >
              {capturing ? "Capturing\u2026" : "Capture current view"}
            </BasicButton>
          )}
        </div>

        {!mulchMapId && (
          <div
            style={{
              padding: "10px 12px",
              background: "#FFF7ED",
              border: "1px solid #FED7AA",
              borderRadius: 6,
              fontSize: 12,
              color: "#8A5300",
              marginBottom: 12,
            }}
          >
            This map hasn&apos;t been saved to a site yet. Save the map
            first, then reopen this dialog to capture snapshots.
          </div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.listLabel}>Saved snapshots</div>
        {loading ? (
          <div style={styles.emptyList}>Loading{"\u2026"}</div>
        ) : rows.length === 0 ? (
          <div style={styles.emptyList}>
            No snapshots yet. Capture one above to get started.
          </div>
        ) : (
          <div style={styles.snapshotGrid}>
            {rows.map((r) => {
              const basemapLabel = r.snapshot.basemap
                ? (BASEMAP_LABELS[r.snapshot.basemap] || r.snapshot.basemap)
                : null;
              return (
                <div key={r.snapshot.id} style={styles.snapshotCard}>
                  <div
                    style={styles.thumb}
                    onClick={() => {
                      // Session 28 hotfix 3: click thumbnail opens the
                      // signed URL in a new tab for a full-size view.
                      // Signed URLs expire in ~1 hour, plenty for the
                      // rep to inspect.
                      if (r.signedUrl) {
                        window.open(r.signedUrl, "_blank", "noopener,noreferrer");
                      }
                    }}
                    title={
                      r.signedUrl
                        ? "Click to open full-size in a new tab"
                        : "Preview unavailable"
                    }
                  >
                    {r.urlLoading ? (
                      <span>Loading{"\u2026"}</span>
                    ) : r.signedUrl ? (
                      <img
                        src={r.signedUrl}
                        alt={`Snapshot from ${moment.utc(r.snapshot.takenAt).local().format("MMM D h:mm a")}`}
                        style={styles.thumbImg}
                      />
                    ) : (
                      <span>Preview unavailable</span>
                    )}
                    {basemapLabel && (
                      <span style={styles.basemapPill}>{basemapLabel}</span>
                    )}
                  </div>
                  <div style={styles.cardFoot}>
                    <div style={styles.cardFootRow}>
                      <span
                        title={new Date(r.snapshot.takenAt).toISOString()}
                        style={{ fontWeight: 500 }}
                      >
                        {moment.utc(r.snapshot.takenAt).fromNow()}
                      </span>
                      <button
                        type="button"
                        style={styles.deleteBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(r.snapshot.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                    <span style={styles.timestampAbsolute}>
                      {moment.utc(r.snapshot.takenAt).local().format("MMM D, YYYY \u00b7 h:mm a")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <BasicButton
          type="button"
          scheme="secondary"
          outline
          onClick={onClose}
          styles={{ margin: "0" }}
          keepText
        >
          Close
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default MapSnapshotsModal;
